import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  Camera, 
  RotateCcw, 
  CheckCircle, 
  Star, 
  Sparkles,
  Target,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Share2,
  Trophy,
  Leaf,
  Zap,
  Eye,
  AlertCircle,
  Loader2,
  Check,
  RefreshCw,
  Download,
  Copy,
  ExternalLink
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useObjectDetection } from '../hooks/useObjectDetection';

interface Mission {
  id: string;
  title: string;
  description: string;
  points: number;
  icon: string;
  verification_method: 'photo' | 'location' | 'quiz' | 'self-report';
  ar_enabled?: boolean;
  target_objects?: string[];
  target_count?: number;
  is_permanent?: boolean;
  category?: string;
  difficulty?: string;
}

interface ImmersiveMissionScreenProps {
  mission: Mission;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (data: any) => Promise<void>;
}

interface DetectedItem {
  id: string;
  type: string;
  confidence: number;
  timestamp: number;
  bbox?: [number, number, number, number];
}

export default function ImmersiveMissionScreen({ 
  mission, 
  isOpen, 
  onClose, 
  onComplete 
}: ImmersiveMissionScreenProps) {
  const [phase, setPhase] = useState<'intro' | 'detection' | 'action_photo' | 'photo_confirm' | 'complete'>('intro');
  const [detectedItems, setDetectedItems] = useState<DetectedItem[]>([]);
  const [actionPhoto, setActionPhoto] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentHint, setCurrentHint] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [recentDetections, setRecentDetections] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [shareCardUrl, setShareCardUrl] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number>();
  const hintIntervalRef = useRef<NodeJS.Timeout>();

  const targetCount = mission.target_count || 1;
  const targetObjects = mission.target_objects || ['item'];
  const IconComponent = (LucideIcons as any)[mission.icon] || Target;

  // Initialize object detection with ONLY COCO-SSD model
  const {
    model,
    isLoading: modelLoading,
    isDetecting,
    startDetection,
    stopDetection,
    isTargetObject
  } = useObjectDetection({
    targetObjects: targetObjects,
    confidenceThreshold: 0.5,
    onDetection: (detections) => {
      if (phase === 'detection' && detectedItems.length < targetCount) {
        detections.forEach(detection => {
          const newItem: DetectedItem = {
            id: `item-${Date.now()}-${Math.random()}`,
            type: detection.class,
            confidence: detection.score,
            timestamp: Date.now(),
            bbox: detection.bbox
          };
          
          setDetectedItems(prev => {
            const isDuplicate = prev.some(item => 
              item.type === newItem.type && 
              (newItem.timestamp - item.timestamp) < 2000
            );
            
            if (!isDuplicate && prev.length < targetCount) {
              if (soundEnabled) {
                playSuccessSound();
              }
              
              setRecentDetections(recent => [...recent.slice(-2), detection.class]);
              return [...prev, newItem];
            }
            return prev;
          });
        });
      }
    }
  });

  const getHintsForPhase = () => {
    if (phase === 'detection') {
      const hintMap: { [key: string]: string[] } = {
        'bottle': [
          'Look for plastic or glass bottles',
          'Water bottles work great',
          'Make sure the bottle is clearly visible',
          'Try different angles for better detection',
          'Good lighting helps detection'
        ],
        'plastic_bottle': [
          'Focus on plastic bottles specifically',
          'Water bottles work great',
          'Make sure the label is visible',
          'Hold the bottle steady',
          'Ensure good lighting'
        ],
        'bag': [
          'Show your reusable shopping bag',
          'Canvas or cloth bags work best',
          'Make sure the bag is clearly visible',
          'Hold the bag open if possible'
        ],
        'vegetables': [
          'Show fresh vegetables or salad',
          'Leafy greens work well',
          'Make sure items are clearly visible',
          'Good lighting helps detection'
        ]
      };
      
      const targetType = targetObjects[0] || 'bottle';
      return hintMap[targetType] || [
        `Look for ${targetType}s around you`,
        'Make sure the object is clearly visible',
        'Good lighting helps with detection',
        'Hold steady for better scanning'
      ];
    } else if (phase === 'action_photo') {
      const actionHints: { [key: string]: string[] } = {
        'Bring Your Own Bag': [
          'Now put your groceries in the reusable bag',
          'Take a photo showing items inside the bag',
          'Show the completed eco-friendly shopping action',
          'Make sure both bag and contents are visible'
        ],
        'Plastic Bottle Collection': [
          'Now put the bottles in a recycling bin',
          'Take a photo of bottles in the recycling container',
          'Show the proper disposal action',
          'Make sure the recycling bin is clearly visible'
        ],
        'Plant-Based Meal': [
          'Now prepare your plant-based meal',
          'Take a photo of the finished dish',
          'Show your delicious eco-friendly meal',
          'Make the meal look appetizing in the photo'
        ]
      };
      
      return actionHints[mission.title] || [
        'Now complete the eco-friendly action',
        'Take a photo showing the completed action',
        'Show how you\'re making a positive impact',
        'Make sure the action is clearly visible'
      ];
    }
    return [];
  };

  useEffect(() => {
    if (isOpen && phase === 'intro') {
      const timer = setTimeout(() => {
        if (!modelLoading) {
          setPhase('detection');
        }
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, phase, modelLoading]);

  useEffect(() => {
    if (phase === 'detection' || phase === 'action_photo') {
      startCamera();
      startHintRotation();
    } else {
      stopCamera();
      stopHintRotation();
    }

    return () => {
      stopCamera();
      stopHintRotation();
    };
  }, [phase]);

  // Check if detection phase is complete and move to action photo
  useEffect(() => {
    console.log('Detection check:', { 
      detectedCount: detectedItems.length, 
      targetCount, 
      phase 
    });
    
    if (detectedItems.length >= targetCount && phase === 'detection') {
      console.log('Detection complete! Moving to action photo phase');
      setPhase('action_photo');
      setIsScanning(false);
      stopDetection();
    }
  }, [detectedItems.length, targetCount, phase]);

  const startCamera = async () => {
    try {
      setCameraError('');
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        videoRef.current.play();
        
        videoRef.current.onloadedmetadata = () => {
          if (model && !modelLoading && phase === 'detection') {
            startScanning();
          }
        };
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    stopDetection();
  };

  const startScanning = () => {
    if (!videoRef.current || !model || modelLoading || phase !== 'detection') return;
    
    console.log('Starting object detection scanning...');
    setIsScanning(true);
    startDetection(videoRef.current, 1000);
    
    const animate = () => {
      if (canvasRef.current && videoRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const video = videoRef.current;
        
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          const time = Date.now() * 0.003;
          const scanY = (Math.sin(time) * 0.5 + 0.5) * canvas.height;
          
          // Green scan line for detection phase
          ctx.strokeStyle = '#10b981';
          ctx.lineWidth = 3;
          ctx.setLineDash([10, 5]);
          ctx.beginPath();
          ctx.moveTo(0, scanY);
          ctx.lineTo(canvas.width, scanY);
          ctx.stroke();
          
          drawDetectionZones(ctx, canvas.width, canvas.height);
        }
      }
      
      if (isScanning && phase === 'detection') {
        animationRef.current = requestAnimationFrame(animate);
      }
    };
    
    animate();
  };

  const drawDetectionZones = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.25;
    
    const isComplete = detectedItems.length >= targetCount;
    ctx.strokeStyle = isComplete ? '#22c55e' : '#10b981';
    ctx.lineWidth = 4;
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();
    
    const pulseRadius = radius + Math.sin(Date.now() * 0.01) * 10;
    ctx.strokeStyle = isComplete ? 'rgba(34, 197, 94, 0.3)' : 'rgba(16, 185, 129, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, pulseRadius, 0, 2 * Math.PI);
    ctx.stroke();
    
    ctx.fillStyle = 'rgba(16, 185, 129, 0.8)';
    ctx.font = '14px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`Looking for: ${targetObjects.join(', ').replace(/_/g, ' ')}`, centerX, centerY - radius - 20);
  };

  const playSuccessSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (error) {
      console.log('Audio not available');
    }
  };

  const playCaptureSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Camera shutter sound effect
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(100, audioContext.currentTime + 0.1);
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.1);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.1);
    } catch (error) {
      console.log('Audio not available');
    }
  };

  const startHintRotation = () => {
    const allHints = getHintsForPhase();
    let hintIndex = 0;
    setCurrentHint(allHints[0] || '');
    
    hintIntervalRef.current = setInterval(() => {
      hintIndex = (hintIndex + 1) % allHints.length;
      setCurrentHint(allHints[hintIndex] || '');
    }, 4000);
  };

  const stopHintRotation = () => {
    if (hintIntervalRef.current) {
      clearInterval(hintIntervalRef.current);
    }
    setCurrentHint('');
  };

  const captureActionPhoto = async () => {
    if (!videoRef.current || isCapturing) return;

    try {
      setIsCapturing(true);
      
      // Play capture sound
      if (soundEnabled) {
        playCaptureSound();
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (ctx && videoRef.current) {
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        ctx.drawImage(videoRef.current, 0, 0);
        
        const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
        setActionPhoto(photoDataUrl);
        
        // Move to photo confirmation phase
        setPhase('photo_confirm');
      }
    } catch (error) {
      console.error('Error capturing photo:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  const confirmPhoto = () => {
    // Photo confirmed, proceed to completion
    handleMissionComplete();
  };

  const retakePhoto = () => {
    // Go back to action photo phase
    setActionPhoto(null);
    setPhase('action_photo');
  };

  const generateShareCard = async () => {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) return null;
      
      // Set canvas size for social media sharing (1200x630 for optimal sharing)
      canvas.width = 1200;
      canvas.height = 630;
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, '#10b981');
      gradient.addColorStop(1, '#059669');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Add pattern overlay
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const size = Math.random() * 4 + 2;
        ctx.beginPath();
        ctx.arc(x, y, size, 0, 2 * Math.PI);
        ctx.fill();
      }
      
      // Title
      ctx.fillStyle = 'white';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('🎉 Mission Complete!', canvas.width / 2, 120);
      
      // Mission title
      ctx.font = 'bold 36px Arial';
      ctx.fillText(mission.title, canvas.width / 2, 180);
      
      // Points earned
      ctx.font = 'bold 32px Arial';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText(`+${mission.points} EcoCoins Earned!`, canvas.width / 2, 240);
      
      // AI verification badge
      ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.fillRect(canvas.width / 2 - 200, 280, 400, 80);
      ctx.fillStyle = 'white';
      ctx.font = '24px Arial';
      ctx.fillText('✅ AI Verified with Photo Proof', canvas.width / 2, 330);
      
      // Category and difficulty
      ctx.font = '20px Arial';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.fillText(`${mission.category || 'Eco'} • ${mission.difficulty || 'Medium'} Difficulty`, canvas.width / 2, 400);
      
      // Detection stats
      if (detectedItems.length > 0) {
        ctx.fillText(`🔍 ${detectedItems.length} items detected with AI`, canvas.width / 2, 440);
      }
      
      // Call to action
      ctx.font = 'bold 28px Arial';
      ctx.fillStyle = '#fbbf24';
      ctx.fillText('Join the eco-revolution at GreenStep!', canvas.width / 2, 520);
      
      // Hashtags
      ctx.font = '18px Arial';
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.fillText('#GreenStep #EcoWarrior #SustainableLiving #ClimateAction', canvas.width / 2, 570);
      
      return canvas.toDataURL('image/png');
    } catch (error) {
      console.error('Error generating share card:', error);
      return null;
    }
  };

  const handleMissionComplete = async () => {
    setIsScanning(false);
    stopDetection();
    setShowConfetti(true);
    
    if (soundEnabled) {
      playCompletionSound();
    }
    
    // Generate share card
    const shareCard = await generateShareCard();
    if (shareCard) {
      setShareCardUrl(shareCard);
    }
    
    setTimeout(() => {
      setPhase('complete');
    }, 2000);
    
    try {
      await onComplete({
        detectedItems: detectedItems.length,
        targetObjects: detectedItems.map(item => item.type),
        confidence: detectedItems.reduce((sum, item) => sum + item.confidence, 0) / detectedItems.length,
        actionPhoto: actionPhoto,
        completionTime: Date.now(),
        arMode: true,
        realDetection: true,
        enhancedAction: true,
        detectionData: detectedItems,
        missionData: {
          id: mission.id,
          title: mission.title,
          description: mission.description,
          points: mission.points,
          category: mission.category || 'General',
          difficulty: mission.difficulty || 'medium'
        }
      });
    } catch (error) {
      console.error('Error completing mission:', error);
    }
  };

  const playCompletionSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const frequencies = [523, 659, 784];
      frequencies.forEach((freq, index) => {
        const osc = audioContext.createOscillator();
        const gain = audioContext.createGain();
        
        osc.connect(gain);
        gain.connect(audioContext.destination);
        
        osc.frequency.setValueAtTime(freq, audioContext.currentTime + index * 0.1);
        gain.gain.setValueAtTime(0.2, audioContext.currentTime + index * 0.1);
        gain.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + index * 0.1 + 0.5);
        
        osc.start(audioContext.currentTime + index * 0.1);
        osc.stop(audioContext.currentTime + index * 0.1 + 0.5);
      });
    } catch (error) {
      console.log('Audio not available');
    }
  };

  const handleRetry = () => {
    setDetectedItems([]);
    setActionPhoto(null);
    setRecentDetections([]);
    setPhase('detection');
  };

  const handleRestart = () => {
    setDetectedItems([]);
    setActionPhoto(null);
    setRecentDetections([]);
    setShowConfetti(false);
    setShareCardUrl(null);
    setPhase('intro');
  };

  const handleShare = () => {
    setShowShareModal(true);
  };

  const shareToSocial = (platform: string) => {
    const shareText = `🎉 Just completed "${mission.title}" on GreenStep! Earned ${mission.points} EcoCoins with AI verification and photo proof! 🌱📸 Join the eco-revolution! #GreenStep #EcoWarrior #SustainableLiving`;
    const shareUrl = window.location.origin;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}&summary=${encodeURIComponent(shareText)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
      telegram: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`
    };
    
    if (urls[platform as keyof typeof urls]) {
      window.open(urls[platform as keyof typeof urls], '_blank', 'width=600,height=400');
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const downloadShareCard = () => {
    if (shareCardUrl) {
      const link = document.createElement('a');
      link.href = shareCardUrl;
      link.download = `greenstep-${mission.title.toLowerCase().replace(/\s+/g, '-')}-completion.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const toggleScanning = () => {
    if (phase === 'detection') {
      if (isScanning) {
        setIsScanning(false);
        stopDetection();
      } else {
        startScanning();
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col overflow-hidden touch-action-manipulation">
      {/* Header - Mobile Optimized */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/70 to-transparent p-3 safe-area-top">
        <div className="flex items-center justify-between text-white">
          <button
            onClick={onClose}
            className="p-3 bg-black/40 rounded-full backdrop-blur-sm hover:bg-black/60 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="h-5 w-5" />
          </button>
          
          <div className="flex items-center space-x-3">
            <div className="bg-black/40 rounded-full px-3 py-2 backdrop-blur-sm">
              <div className="flex items-center space-x-2">
                <Eye className="h-4 w-4 text-emerald-300" />
                <span className="text-sm font-medium">
                  {phase === 'detection' ? 'AI Detection' : 
                   phase === 'action_photo' ? 'Action Photo' : 
                   phase === 'photo_confirm' ? 'Confirm Photo' : 'Complete'}
                </span>
                {modelLoading && <Loader2 className="h-3 w-3 animate-spin" />}
              </div>
            </div>
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-3 bg-black/40 rounded-full backdrop-blur-sm hover:bg-black/60 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mission Introduction Phase */}
      {phase === 'intro' && (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-emerald-900 via-teal-800 to-blue-900 relative overflow-hidden p-4">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-emerald-400 rounded-full blur-3xl animate-pulse"></div>
            <div className="absolute bottom-1/3 right-1/4 w-24 h-24 bg-teal-400 rounded-full blur-2xl animate-pulse delay-1000"></div>
          </div>
          
          <div className="text-center text-white z-10 px-4 max-w-sm mx-auto mobile-scroll">
            <div className="mb-6 relative">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto backdrop-blur-sm border border-white/20">
                <IconComponent className="h-10 w-10 text-emerald-300" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full flex items-center justify-center">
                <Eye className="h-4 w-4 text-white" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold mb-3 bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
              Enhanced AR Mission
            </h1>
            <h2 className="text-xl font-semibold mb-4">{mission.title}</h2>
            
            <p className="text-base text-emerald-100 mb-6 leading-relaxed">
              {mission.description}
            </p>
            
            <div className="bg-white/10 rounded-2xl p-4 backdrop-blur-sm border border-white/20 mb-6">
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                  <span className="text-sm text-emerald-200">AI detects required objects</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm">2</div>
                  <span className="text-sm text-emerald-200">Take photo of completed eco-action</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-sm">3</div>
                  <span className="text-sm text-emerald-200">Confirm photo and complete mission</span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="flex items-center justify-between">
                  <span className="text-emerald-200 text-sm">Reward:</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                    <span className="font-bold text-yellow-300">{mission.points} EcoCoins</span>
                  </div>
                </div>
              </div>
            </div>
            
            {modelLoading ? (
              <div className="mt-6">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Loader2 className="h-5 w-5 animate-spin text-emerald-300" />
                  <span className="text-emerald-200">Loading AI model...</span>
                </div>
                <p className="text-sm text-emerald-300">Preparing object detection</p>
              </div>
            ) : (
              <div className="mt-6">
                <div className="w-16 h-1 bg-white/20 rounded-full mx-auto overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full animate-pulse"></div>
                </div>
                <p className="text-sm text-emerald-200 mt-2">Starting AI detection...</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Detection Phase */}
      {phase === 'detection' && (
        <div className="flex-1 relative">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          
          <canvas
            ref={canvasRef}
            className="absolute inset-0 w-full h-full pointer-events-none"
          />
          
          {cameraError && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4">
              <div className="text-center text-white max-w-sm">
                <Camera className="h-16 w-16 mx-auto mb-4 text-red-400" />
                <h3 className="text-xl font-bold mb-2">Camera Access Required</h3>
                <p className="text-gray-300 mb-6">{cameraError}</p>
                <button
                  onClick={startCamera}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-lg font-medium transition-colors min-h-[44px]"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}
          
          {modelLoading && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4">
              <div className="text-center text-white max-w-sm">
                <Loader2 className="h-16 w-16 mx-auto mb-4 text-emerald-400 animate-spin" />
                <h3 className="text-xl font-bold mb-2">Loading AI Model</h3>
                <p className="text-gray-300">Preparing object detection...</p>
              </div>
            </div>
          )}
          
          {/* Progress HUD - Mobile Optimized */}
          <div className="absolute top-16 left-3 right-3 safe-area-top">
            <div className="bg-black/60 rounded-2xl p-3 backdrop-blur-sm border border-white/20">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Eye className="h-4 w-4 text-emerald-300" />
                <span className="text-white font-medium text-sm">
                  AI Detection: Finding objects
                </span>
                {isDetecting && <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>}
              </div>
              
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium text-sm">
                  {detectedItems.length} of {targetCount} items found
                </span>
                <span className="text-emerald-300 font-bold text-sm">
                  {Math.round((detectedItems.length / targetCount) * 100)}%
                </span>
              </div>
              
              <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full transition-all duration-500"
                  style={{ width: `${(detectedItems.length / targetCount) * 100}%` }}
                />
              </div>
              
              {recentDetections.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {recentDetections.map((detection, index) => (
                    <span 
                      key={index}
                      className="bg-green-500/80 text-white px-2 py-1 rounded-full text-xs animate-pulse"
                    >
                      ✓ {detection}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Detection Feedback - Mobile Optimized */}
          {detectedItems.map((item, index) => (
            <div
              key={item.id}
              className="absolute top-32 right-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-3 py-2 rounded-lg font-medium animate-bounce safe-area-top"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                transform: `translateY(${index * 50}px)`
              }}
            >
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Found {item.type}!</span>
              </div>
            </div>
          ))}
          
          {/* Hint Display - Mobile Optimized */}
          {currentHint && (
            <div className="absolute bottom-24 left-3 right-3 safe-area-bottom">
              <div className="bg-black/60 rounded-xl p-3 backdrop-blur-sm border border-white/20 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-emerald-300" />
                  <span className="text-emerald-300 text-sm font-medium">Detection Tip</span>
                </div>
                <p className="text-white text-sm">{currentHint}</p>
              </div>
            </div>
          )}
          
          {/* Action Buttons - Mobile Optimized */}
          <div className="absolute bottom-6 left-3 right-3 safe-area-bottom">
            <div className="flex items-center justify-center space-x-3">
              <button
                onClick={handleRetry}
                className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-full backdrop-blur-sm border border-white/20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <RotateCcw className="h-5 w-5" />
              </button>
              
              <button
                onClick={toggleScanning}
                disabled={modelLoading}
                className={`p-4 rounded-full backdrop-blur-sm border border-white/20 transition-colors min-w-[60px] min-h-[60px] flex items-center justify-center ${
                  isScanning 
                    ? 'bg-red-500/80 hover:bg-red-500' 
                    : 'bg-gradient-to-r from-emerald-500/80 to-teal-500/80 hover:from-emerald-500 hover:to-teal-500'
                } ${modelLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {modelLoading ? (
                  <Loader2 className="h-6 w-6 text-white animate-spin" />
                ) : isScanning ? (
                  <Pause className="h-6 w-6 text-white" />
                ) : (
                  <Play className="h-6 w-6 text-white" />
                )}
              </button>
              
              {detectedItems.length >= targetCount && (
                <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3 rounded-full font-medium flex items-center space-x-2 min-h-[44px]">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm">Detection Complete!</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Action Photo Phase */}
      {phase === 'action_photo' && (
        <div className="flex-1 relative">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            playsInline
            muted
          />
          
          {/* Action Photo Instructions */}
          <div className="absolute top-16 left-3 right-3 safe-area-top">
            <div className="bg-black/60 rounded-2xl p-4 backdrop-blur-sm border border-white/20">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Camera className="h-5 w-5 text-blue-300" />
                <span className="text-white font-medium text-sm">Action Photo Required</span>
              </div>
              
              <div className="text-center">
                <h3 className="text-white font-bold text-lg mb-2">Complete the Eco-Action!</h3>
                <p className="text-blue-200 text-sm mb-3">
                  {phase === 'action_photo' && mission.title === 'Bring Your Own Bag' && 
                    'Put your groceries in the reusable bag and take a photo'}
                  {phase === 'action_photo' && mission.title === 'Plastic Bottle Collection' && 
                    'Put the bottles in a recycling bin and take a photo'}
                  {phase === 'action_photo' && mission.title === 'Plant-Based Meal' && 
                    'Prepare your plant-based meal and take a photo'}
                  {phase === 'action_photo' && !['Bring Your Own Bag', 'Plastic Bottle Collection', 'Plant-Based Meal'].includes(mission.title) && 
                    'Complete the eco-friendly action and take a photo as proof'}
                </p>
                
                <div className="bg-blue-500/20 rounded-lg p-3">
                  <p className="text-blue-200 text-xs">
                    ✅ Objects detected: {detectedItems.map(item => item.type).join(', ')}
                  </p>
                  <p className="text-blue-200 text-xs mt-1">
                    📸 Now show the completed action!
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Hint Display for Action Phase */}
          {currentHint && (
            <div className="absolute bottom-32 left-3 right-3 safe-area-bottom">
              <div className="bg-black/60 rounded-xl p-3 backdrop-blur-sm border border-white/20 text-center">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <Camera className="h-4 w-4 text-blue-300" />
                  <span className="text-blue-300 text-sm font-medium">Action Tip</span>
                </div>
                <p className="text-white text-sm">{currentHint}</p>
              </div>
            </div>
          )}
          
          {/* Capture Button */}
          <div className="absolute bottom-6 left-3 right-3 safe-area-bottom">
            <div className="flex items-center justify-center">
              <button
                onClick={captureActionPhoto}
                disabled={isCapturing}
                className="bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 disabled:opacity-50 text-white px-8 py-4 rounded-full font-medium transition-colors flex items-center space-x-3 min-h-[60px] shadow-lg"
              >
                {isCapturing ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span className="text-lg">Capturing...</span>
                  </>
                ) : (
                  <>
                    <Camera className="h-6 w-6" />
                    <span className="text-lg">Capture Action Photo</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Confirmation Phase */}
      {phase === 'photo_confirm' && actionPhoto && (
        <div className="flex-1 relative bg-black">
          {/* Captured Photo Display */}
          <div className="flex-1 flex items-center justify-center p-4">
            <img
              src={actionPhoto}
              alt="Captured action photo"
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            />
          </div>
          
          {/* Photo Confirmation Controls */}
          <div className="absolute top-16 left-3 right-3 safe-area-top">
            <div className="bg-black/60 rounded-2xl p-4 backdrop-blur-sm border border-white/20 text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <CheckCircle className="h-5 w-5 text-green-300" />
                <span className="text-white font-medium text-sm">Photo Captured!</span>
              </div>
              <p className="text-white text-sm">
                Review your action photo. Does it clearly show the completed eco-action?
              </p>
            </div>
          </div>
          
          {/* Confirmation Buttons */}
          <div className="absolute bottom-6 left-3 right-3 safe-area-bottom">
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={retakePhoto}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-4 rounded-xl font-medium transition-colors flex items-center space-x-2 backdrop-blur-sm border border-white/20 min-h-[44px]"
              >
                <RefreshCw className="h-5 w-5" />
                <span>Retake Photo</span>
              </button>
              
              <button
                onClick={confirmPhoto}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-8 py-4 rounded-xl font-medium transition-colors flex items-center space-x-2 min-h-[44px] shadow-lg"
              >
                <Check className="h-5 w-5" />
                <span>Confirm & Complete</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mission Complete Phase */}
      {phase === 'complete' && (
        <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-emerald-600 via-teal-500 to-blue-500 relative overflow-hidden p-4">
          {showConfetti && (
            <div className="absolute inset-0 pointer-events-none">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-yellow-400 rounded-full animate-bounce"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${1 + Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          )}
          
          <div className="text-center text-white z-10 px-4 max-w-sm mx-auto mobile-scroll">
            <div className="mb-6 relative">
              <div className="w-24 h-24 bg-white/10 rounded-full flex items-center justify-center mx-auto backdrop-blur-sm border border-white/20">
                <Trophy className="h-12 w-12 text-yellow-300" />
              </div>
              <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-full flex items-center justify-center animate-pulse">
                <CheckCircle className="h-6 w-6 text-white" />
              </div>
            </div>
            
            <h1 className="text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              Mission Complete! 🎉
            </h1>
            <h2 className="text-xl font-semibold mb-6">{mission.title}</h2>
            
            <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/20 max-w-md mx-auto mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-300">{detectedItems.length}</div>
                  <div className="text-xs text-white/80">Objects Detected</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-300">+{mission.points}</div>
                  <div className="text-xs text-white/80">EcoCoins</div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-white/20">
                <div className="text-xs text-white/80 mb-2">Completed Actions:</div>
                <div className="flex flex-wrap gap-2">
                  <span className="bg-emerald-500/30 text-emerald-200 px-2 py-1 rounded-full text-xs">
                    ✅ AI Detection
                  </span>
                  <span className="bg-blue-500/30 text-blue-200 px-2 py-1 rounded-full text-xs">
                    📸 Action Photo
                  </span>
                  <span className="bg-purple-500/30 text-purple-200 px-2 py-1 rounded-full text-xs">
                    ✅ Photo Confirmed
                  </span>
                  <span className="bg-green-500/30 text-green-200 px-2 py-1 rounded-full text-xs">
                    🌱 Eco Impact
                  </span>
                </div>
              </div>
            </div>
            
            <div className="bg-emerald-500/20 rounded-xl p-4 mb-6 border border-emerald-400/30">
              <div className="flex items-center justify-center space-x-2 mb-2">
                <Leaf className="h-5 w-5 text-emerald-300" />
                <span className="font-medium text-emerald-200">Environmental Impact</span>
              </div>
              <p className="text-white text-sm">
                You've completed a verified eco-action with AI detection and photo proof! Your positive impact is making a real difference. 🌱📸✅
              </p>
            </div>
            
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleShare}
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-4 rounded-xl font-medium transition-colors flex items-center justify-center space-x-2 backdrop-blur-sm border border-white/20 min-h-[44px]"
              >
                <Share2 className="h-5 w-5" />
                <span>Share Achievement</span>
              </button>
              
              <button
                onClick={mission.is_permanent ? handleRestart : onClose}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white px-6 py-4 rounded-xl font-medium transition-colors min-h-[44px]"
              >
                {mission.is_permanent ? (
                  <>
                    <RefreshCw className="h-5 w-5 mr-2 inline-block" />
                    Complete This Mission Again
                  </>
                ) : (
                  "Continue Your Journey"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareCardUrl && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-gray-900">Share Your Achievement</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Share Card Preview */}
              <div className="bg-gray-100 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-2">Share Card Preview:</p>
                <img 
                  src={shareCardUrl} 
                  alt="Mission completion share card" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
              </div>
              
              {/* Share Options */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Share to:</h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => shareToSocial('twitter')}
                    className="flex flex-col items-center justify-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-6 w-6 text-blue-500 mb-1" />
                    <span className="text-sm text-gray-700">Twitter</span>
                  </button>
                  <button
                    onClick={() => shareToSocial('facebook')}
                    className="flex flex-col items-center justify-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-6 w-6 text-blue-700 mb-1" />
                    <span className="text-sm text-gray-700">Facebook</span>
                  </button>
                  <button
                    onClick={() => shareToSocial('linkedin')}
                    className="flex flex-col items-center justify-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-6 w-6 text-blue-600 mb-1" />
                    <span className="text-sm text-gray-700">LinkedIn</span>
                  </button>
                  <button
                    onClick={() => shareToSocial('whatsapp')}
                    className="flex flex-col items-center justify-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-6 w-6 text-green-500 mb-1" />
                    <span className="text-sm text-gray-700">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => shareToSocial('telegram')}
                    className="flex flex-col items-center justify-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                  >
                    <ExternalLink className="h-6 w-6 text-blue-500 mb-1" />
                    <span className="text-sm text-gray-700">Telegram</span>
                  </button>
                  <button
                    onClick={() => copyToClipboard(`🎉 Just completed "${mission.title}" on GreenStep! Earned ${mission.points} EcoCoins with AI verification and photo proof! 🌱📸 Join the eco-revolution! #GreenStep #EcoWarrior #SustainableLiving`)}
                    className="flex flex-col items-center justify-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Copy className="h-6 w-6 text-gray-500 mb-1" />
                    <span className="text-sm text-gray-700">Copy Text</span>
                  </button>
                </div>
              </div>
              
              {/* Download Option */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={downloadShareCard}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Download className="h-5 w-5" />
                  <span>Download Share Card</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}